import MDBox from "components/MDBox";
import { useState } from "react";
import BasicTabs from "ui-components/tabs/BasicTabs";
import { ListLayout } from "ui-components/Layout";
import { useLocation } from "react-router-dom";
import EditBillingChargeable, { validateData } from "./edit-billing-chargeable";
import BillingInvoiceHeader from "./billing-invoice-header";
import { editBillingChargeable } from "services/data.billing.service";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Success } from "ui-components/Alert";
import { Error_ } from "ui-components/Alert";
import dayjs from "dayjs";
import { MessageDialog } from "ui-components/Alert";
import { useCustomerValue } from "providers/CustomerContext";
import { useTeamValue } from "providers/TeamContext";

const tabs = [
  "Header",
  "Storage",
  "Inbound",
  "Outbound (B2B)",
  "Outbound (B2C)",
  "Return",
  "Stuffing & Unstuffing",
  "Packaging Material",
  "VAS",
  "Management Fees",
  "OT",
];

function EditBillingInvoice() {
  const {permissions, allowAll} = useTeamValue();
  const viewPricePermission =
  (Boolean(permissions["Team Role"]) && permissions["Team Role"].some(a => a === "access")) ||
     (Boolean(permissions["Billing Invoice"]) &&
      permissions["Billing Invoice"].findIndex((b) => b === "viewPrice") !== -1);

  const [status, setStatus] = useState("Header");
  const {customerCode} = useCustomerValue();
  
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);

  const location = useLocation();
  const invoiceNumber = location.state?.invoiceNumber;

  const [localData, setLocalData] = useState([]);
  const [chargeableData, setChargeableData] = useState([]);
  const queryClient = useQueryClient();

  const editBillingChargeableMutation = useMutation(editBillingChargeable, {
    onSuccess: (data) => {
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError: (error) => {
      setAlert(<Error_ data={error} onChange={setAlert} />);
    },
  });

  const handleSubmit = async () => {
    try {
      window.scrollTo(0, 0);
      const validate = validateData(localData);
      if (validate.length > 0) {
        setAlert(<Error_ content={validate.join(",")} onChange={setAlert} />);
        return;
      }

      const modified = localData.map(x => {
        const {documentDate, ...other} = x;
        return {
          documentDate: documentDate ?  dayjs(documentDate).format("YYYY-MM-DD") : null,
          ...other
        }
      })

      // Call the API to add data
      editBillingChargeableMutation.mutate({invoiceNumber, billCategory: status, details: modified}, {
        onSuccess: (data) => {
          queryClient.invalidateQueries(["billing-chargeable"])
          setAlert(<Success data={data} onChange={setAlert} />);
        },
        onError: (error) => {
          setAlert(<Error_ data={error} onChange={setAlert} />);
        },
      });
    } catch (error) {
      console.error("Error saving changes:", error);
      setAlert(<Error_ data={error} onChange={setAlert} />);
    }
  };

  function deepEqual(obj1, obj2) {
    if (obj1 === obj2) return true; // Check reference equality
    if (typeof obj1 !== "object" || typeof obj2 !== "object" || obj1 == null || obj2 == null) {
      return false; // Handle primitive types and null
    }
    
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    
    if (keys1.length !== keys2.length) return false;
  
    return keys1.every(key => deepEqual(obj1[key], obj2[key]));
  }
  
  function arraysAreEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;
  
    return arr1.every((obj1, index) => deepEqual(obj1, arr2[index]));
  }

  const handleStatusChange = (newValue) => {
    if (!arraysAreEqual(chargeableData, localData)){
      setDialog(
        <MessageDialog
          title={`Unsaved Changes for ${status}. Save changes?`}
          onClose={() => {
            setDialog(null);
            setStatus(newValue)
          }}
          onOk={() => {
            handleSubmit()
            setDialog(null);
            setStatus(newValue);
          }}
        />
      );
    }
    else
      setStatus(newValue)
  }

  const render = () => {
    let task;
    switch (status) {
      case "Header":
        task = (
          <BillingInvoiceHeader
            invoiceNumber={invoiceNumber}
            viewPricePermission={viewPricePermission}
          />
        );
        break;
      default:
        task = (
          <EditBillingChargeable
            activeTab={status}
            invoiceNumber={invoiceNumber}
            localData={localData}
            setLocalData={setLocalData}
            setChargeableData={setChargeableData}
            handleSubmit={handleSubmit}
            setAlert={setAlert}
            customerCode={customerCode}
            viewPricePermission={viewPricePermission}
          />
        );
        break;
    }

    return task;
  };
  return (
    <ListLayout lg={12}>
      <MDBox my={3}>
        {alert}
        {dialog}
        <BasicTabs tabs={tabs} status={status} setStatus={handleStatusChange} />

        <MDBox>{render()}</MDBox>
      </MDBox>
    </ListLayout>
  );
}

export default EditBillingInvoice;
